import {useEffect,useState} from 'react';
import {AnimatePresence,motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './SafeIcon';

const {FiChevronLeft,FiChevronRight,FiArrowUpRight} = FiIcons;

const slides = [
  {image: 'https://images.unsplash.com/photo-1469334031218-e382a71b716b?auto=format&fit=crop&w=1600&q=85',title: 'The City Edit',note: 'Effortless layers for bright mornings'},
  {image: 'https://images.unsplash.com/photo-1490481651871-ab68de25d43d?auto=format&fit=crop&w=1600&q=85',title: 'Pastel Season',note: 'Soft rose,cream and sage tones'},
  {image: 'https://images.unsplash.com/photo-1445205170230-053b83016050?auto=format&fit=crop&w=1600&q=85',title: 'Occasion Wear',note: 'Dresses that arrive before the invite'},
  {image: 'https://images.unsplash.com/photo-1496747611176-843222e1e57c?auto=format&fit=crop&w=1600&q=85',title: 'Evening Story',note: 'Silk,shadow and a little sparkle'}
];

const WA = 'https://wa.me/919876543210?text=Hi%20Aurelle!%20I%20saw%20a%20look%20in%20your%20lookbook%20and%20would%20love%20to%20style%20it.';

function LookbookSlider() {
  const [index,setIndex] = useState(0);
  const [dir,setDir] = useState(1);
  const [paused,setPaused] = useState(false);

  const go = (n) => {
    setDir(n > index ? 1 : -1);
    setIndex(((n % slides.length) + slides.length) % slides.length);
  };

  useEffect(() => {
    if (paused) return;
    const t = setTimeout(() => {
      setDir(1);
      setIndex(i => (i + 1) % slides.length);
    },5500);
    return () => clearTimeout(t);
  },[index,paused]);

  const s = slides[index];

  return (
    <section id="lookbook" className="bg-[#1F1B17] py-24 md:py-32 px-6 md:px-12 lg:px-20" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div className="max-w-7xl mx-auto">
        <div className="mb-12 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <p className="eyebrow mb-4" style={{color: '#B87333'}}>The lookbook</p>
            <h2 className="font-display text-[clamp(36px,5vw,64px)] leading-tight text-[#F7F2EA] font-light">
              This season,<em className="italic text-[#B87333]">styled by us</em>
            </h2>
          </div>
          <p className="max-w-xs font-sans text-sm text-[#F7F2EA]/50 leading-relaxed">Four edits,photographed in real light. See a look you love? One tap takes you to our stylists.</p>
        </div>
        <div className="relative h-[60vh] min-h-[420px] overflow-hidden">
          <AnimatePresence custom={dir}>
            <motion.img key={index} src={s.image} alt={s.title} initial={{opacity: 0,x: dir * 80}} animate={{opacity: 1,x: 0}} exit={{opacity: 0,x: dir * -80}} transition={{duration: 0.6,ease: 'easeOut'}} className="absolute inset-0 w-full h-full object-cover" />
          </AnimatePresence>
          <div className="absolute inset-0 bg-gradient-to-t from-[#1F1B17]/85 via-transparent to-[#1F1B17]/25" />
          <span className="absolute right-5 top-5 bg-[#1F1B17]/50 backdrop-blur px-4 py-2 font-sans text-[11px] tracking-[0.3em] text-[#F7F2EA]">
            {String(index + 1).padStart(2,'0')} / {String(slides.length).padStart(2,'0')}
          </span>
          <div className="absolute bottom-10 left-6 md:left-12 max-w-md">
            <h3 className="font-display text-4xl md:text-5xl text-[#F7F2EA] font-light">{s.title}</h3>
            <p className="mt-3 font-sans text-sm text-[#F7F2EA]/70">{s.note}</p>
            <a href={WA} target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center gap-3 bg-[#B87333] px-7 py-3.5 font-sans text-[11px] tracking-[0.2em] uppercase text-[#F7F2EA] transition hover:bg-[#1F3D2E]">
              Style this look <SafeIcon icon={FiArrowUpRight} />
            </a>
          </div>
          <button onClick={() => go(index - 1)} aria-label="Previous" className="absolute left-4 top-1/2 -translate-y-1/2 grid place-items-center w-12 h-12 rounded-full border border-[#F7F2EA]/40 bg-[#1F1B17]/30 text-[#F7F2EA] backdrop-blur transition hover:bg-[#B87333] hover:text-[#1F1B17] hover:border-[#B87333]">
            <SafeIcon icon={FiChevronLeft} className="text-xl" />
          </button>
          <button onClick={() => go(index + 1)} aria-label="Next" className="absolute right-4 top-1/2 -translate-y-1/2 grid place-items-center w-12 h-12 rounded-full border border-[#F7F2EA]/40 bg-[#1F1B17]/30 text-[#F7F2EA] backdrop-blur transition hover:bg-[#B87333] hover:text-[#1F1B17] hover:border-[#B87333]">
            <SafeIcon icon={FiChevronRight} className="text-xl" />
          </button>
          <div className="absolute bottom-0 left-0 h-[3px] w-full bg-[#F7F2EA]/10">
            <div className="h-full bg-[#B87333] transition-all duration-500" style={{width: `${((index + 1) / slides.length) * 100}%`}} />
          </div>
        </div>
        <div className="mt-5 flex gap-3 overflow-x-auto pb-2">
          {slides.map((item,i) => (
            <button key={item.title} onClick={() => go(i)} aria-label={`Go to ${item.title}`} className={`h-20 w-32 flex-none overflow-hidden transition ${i === index ? 'ring-2 ring-[#B87333]' : 'opacity-40 hover:opacity-80'}`}>
              <img src={item.image} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

export default LookbookSlider;
